// ./src/utils/pwaDetection.ts

export type AppDisplayMode = 'browser' | 'standalone' | 'fullscreen' | 'minimal-ui';

export interface PWAInfo {
  isPWA: boolean;
  displayMode: AppDisplayMode;
  isIOSPWA: boolean;
  isAndroidPWA: boolean;
  canInstall: boolean;
  isSecureContext: boolean;
  serviceWorkerSupported: boolean;
  hasActiveServiceWorker: boolean;
  platform: 'ios' | 'android' | 'windows' | 'macos' | 'linux' | 'unknown';
  isMobile: boolean;
  isTablet: boolean;
}

function detectPlatform(): PWAInfo['platform'] {
  const ua = navigator.userAgent;

  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Windows/.test(ua)) return 'windows';
  if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(ua)) return 'macos';
  if (/Linux/.test(ua)) return 'linux';

  return 'unknown';
}

function detectDeviceType(): { isMobile: boolean; isTablet: boolean } {
  const ua = navigator.userAgent;

  const isTablet = /iPad/.test(ua) ||
    (/Android/.test(ua) && !/Mobile/.test(ua)) ||
    /Tablet/.test(ua);

  const isMobile = !isTablet && (
    /iPhone|iPod|Android.*Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)
  );

  return { isMobile, isTablet };
}

function getDisplayMode(): AppDisplayMode {
  if (window.matchMedia('(display-mode: fullscreen)').matches) return 'fullscreen';
  if (window.matchMedia('(display-mode: standalone)').matches) return 'standalone';
  if (window.matchMedia('(display-mode: minimal-ui)').matches) return 'minimal-ui';
  if ((window.navigator as any).standalone === true) return 'standalone';

  return 'browser';
}

export function getPWAInfo(): PWAInfo {
  const displayMode = getDisplayMode();
  const platform = detectPlatform();
  const { isMobile, isTablet } = detectDeviceType();
  const isIOSStandalone = (window.navigator as any).standalone === true;

  const isPWA = displayMode !== 'browser';

  return {
    isPWA,
    displayMode,
    isIOSPWA: platform === 'ios' && isIOSStandalone,
    isAndroidPWA: platform === 'android' && displayMode === 'standalone',
    canInstall: 'BeforeInstallPromptEvent' in window || platform === 'ios',
    isSecureContext: window.isSecureContext,
    serviceWorkerSupported: 'serviceWorker' in navigator,
    hasActiveServiceWorker: Boolean(navigator.serviceWorker?.controller),
    platform,
    isMobile,
    isTablet,
  };
}

export function onDisplayModeChange(callback: (mode: AppDisplayMode) => void): () => void {
  const mediaQueries = [
    window.matchMedia('(display-mode: standalone)'),
    window.matchMedia('(display-mode: fullscreen)'),
    window.matchMedia('(display-mode: minimal-ui)'),
  ];

  const handler = () => callback(getDisplayMode());

  mediaQueries.forEach(mq => mq.addEventListener('change', handler));

  return () => {
    mediaQueries.forEach(mq => mq.removeEventListener('change', handler));
  };
}

/** Check for service worker updates */
export async function checkForServiceWorkerUpdate(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    if (registration) {
      await registration.update();
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to check for SW updates:', error);
    return false;
  }
}